import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useAuth } from '../../auth/AuthContext';
import { sanitizeRichText } from '../../utils/sanitizeHtml';
import { mediaSrc } from '../../api/mediaUrl';
import { useApiComments } from '../../api/useApiComments';
import {
  createComment,
  editComment,
  deleteComment,
  markThreadRead,
  reportComment,
  type CommentDto,
  type CommentContextMode,
  type ReactionVocabItem,
} from '../../api/comments';
import { ReactionBar } from './ReactionBar';
import { PostReactionButton } from './PostReactionButton';
import styles from './ConversationModal.module.css';

// =============================================================================
// ConversationModal (DEV-720). The single standard comment modal across feed,
// community feed, Journal, and Role Digest. Fixed-height shell (90vh) — the
// header and composer are pinned; only the post + thread scroll, so it never
// resizes as comments load. Optional post preview pins at the top with a
// like/comment/share action row (post-level reactions).
// =============================================================================

export interface ResolvedUser {
  name?: string;
  avatarUrl?: string;
}

/** The underlying post, supplied by the surface that opens the modal. */
export interface PostPreview {
  authorName?: string;
  authorAvatarUrl?: string | null;
  /** Link to the author's profile / community page (avatar + name become a link). */
  authorHref?: string | null;
  dateText?: string;
  body?: string | null;
  imageUrl?: string | null;
  /** External destination, if this post links out. Shown as a visible,
   *  one-tap "Open link" affordance (domain shown) rather than ejecting the
   *  user on click — keeps them in-platform with the link honest + accessible. */
  linkUrl?: string | null;
}

interface ConversationModalProps {
  anchorType: string;
  anchorId: string;
  open: boolean;
  onClose: () => void;
  title?: string;
  post?: PostPreview;
  resolveUser?: (guid: string) => ResolvedUser | undefined;
  /** 'modal' (default) = centered dialog. 'theater' = full-screen media stage
   *  (image left/top) + comments panel — used when a post has a hero image. */
  layout?: 'modal' | 'theater';
  /** When true (and onDelete given), the post header shows a "..." menu with
   *  Delete — carried over from the feed card so the author can delete here too. */
  canDelete?: boolean;
  onDelete?: () => void;
}

/** Best-effort hostname for a "Open link ↗ {domain}" affordance. */
function linkDomain(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  return d.toLocaleDateString();
}

export function ConversationModal({
  anchorType,
  anchorId,
  open,
  onClose,
  title,
  post,
  resolveUser,
  layout = 'modal',
  canDelete,
  onDelete,
}: ConversationModalProps) {
  const { t } = useAppTranslation('common');
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentUserGuid = user?.userGuid ?? '';
  const isSystemAdmin = user?.isSystemAdmin ?? false;

  const goAuthor = () => {
    const href = post?.authorHref;
    if (!href) return;
    onClose();
    if (href.startsWith('/')) navigate(href);
    else window.open(href, '_blank', 'noopener,noreferrer');
  };

  // Theater applies only when there's a hero image to stage.
  const isTheater = layout === 'theater' && !!post?.imageUrl;
  const [zoom, setZoom] = useState(1);
  const stageRef = useRef<HTMLDivElement>(null);

  // Post "..." menu (Delete).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  useEffect(() => { if (open) setZoom(1); }, [open]);

  const toggleFullscreen = () => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else el.requestFullscreen?.().catch(() => {});
  };

  const { thread, loading, error, refetch } = useApiComments(
    open ? anchorType : null,
    open ? anchorId : null,
  );

  const [newBody, setNewBody] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open) return;
    markThreadRead(anchorType, anchorId).catch(() => {});
  }, [open, anchorType, anchorId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const { roots, repliesByParent } = useMemo(() => {
    const comments = thread?.comments ?? [];
    const r: CommentDto[] = [];
    const map = new Map<string, CommentDto[]>();
    for (const c of comments) {
      if (c.parentCommentId) {
        const arr = map.get(c.parentCommentId) ?? [];
        arr.push(c);
        map.set(c.parentCommentId, arr);
      } else {
        r.push(c);
      }
    }
    return { roots: r, repliesByParent: map };
  }, [thread]);

  if (!open) return null;

  const mode: CommentContextMode = thread?.contextMode ?? 'project';
  const vocab: ReactionVocabItem[] = thread?.reactionVocab ?? [];
  const postReactions = thread?.postReactions ?? [];
  const commentCount = thread?.comments?.length ?? 0;
  const presentFaces = postReactions.filter((r) => r.count > 0).map((r) => r.reactionType);

  const headerTitle = post?.authorName
    ? t('comments.postTitle', { name: post.authorName })
    : title ?? t('comments.title');

  const post_ = async (body: string, parentId: string | null) => {
    const text = body.trim();
    if (!text) return;
    await createComment(anchorType, anchorId, text, parentId);
    refetch();
  };

  const onPostRoot = async () => {
    if (posting) return;
    setPosting(true);
    try {
      await post_(newBody, null);
      setNewBody('');
    } finally {
      setPosting(false);
    }
  };

  const onShare = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: headerTitle, url }).catch(() => {});
    else navigator.clipboard?.writeText(url).catch(() => {});
  };

  // ── Shared content fragments (composed differently per layout) ──────
  const postHeadEl = post?.authorName ? (
    <div className={styles.postHead}>
      {post.authorHref ? (
        <button type="button" className={styles.authorLink} onClick={goAuthor}>
          {post.authorAvatarUrl ? (
            <img className={styles.postAvatar} src={mediaSrc(post.authorAvatarUrl) ?? undefined} alt="" />
          ) : (
            <div className={styles.postAvatar}>{initials(post.authorName ?? '?')}</div>
          )}
          <div className={styles.postMeta}>
            <div className={styles.postAuthor}>{post.authorName}</div>
            {post.dateText && <div className={styles.postDate}>{post.dateText}</div>}
          </div>
        </button>
      ) : (
        <>
          {post.authorAvatarUrl ? (
            <img className={styles.postAvatar} src={mediaSrc(post.authorAvatarUrl) ?? undefined} alt="" />
          ) : (
            <div className={styles.postAvatar}>{initials(post.authorName ?? '?')}</div>
          )}
          <div className={styles.postMeta}>
            <div className={styles.postAuthor}>{post.authorName}</div>
            {post.dateText && <div className={styles.postDate}>{post.dateText}</div>}
          </div>
        </>
      )}
      {canDelete && onDelete && (
        <div className={styles.postMenuWrap} ref={menuRef}>
          <button
            type="button"
            className={styles.postMenuTrigger}
            aria-label={t('comments.postMenu')}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <i className="ti ti-dots" aria-hidden="true" />
          </button>
          {menuOpen && (
            <div className={styles.postMenuDropdown}>
              <button
                type="button"
                className={styles.postMenuDanger}
                onClick={() => { setMenuOpen(false); onDelete(); }}
              >
                <i className="ti ti-trash" aria-hidden="true" /> {t('comments.deletePost')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  ) : null;

  // Honest, visible outbound link (domain shown) — keeps the user in-platform
  // by default while making the destination one obvious tap away.
  const openLinkEl = post?.linkUrl ? (
    <a
      className={styles.openLink}
      href={post.linkUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      <i className="ti ti-external-link" aria-hidden="true" />
      <span>{t('comments.openLink')}</span>
      <span className={styles.openLinkDomain}>{linkDomain(post.linkUrl)}</span>
    </a>
  ) : null;

  const actionRowEl = (
    <div className={styles.actionRow}>
      <PostReactionButton
        anchorType={anchorType}
        anchorId={anchorId}
        reactions={postReactions}
        vocab={vocab}
        onChanged={refetch}
      />
      <span className={styles.actionBtn} aria-hidden="true">
        <i className="ti ti-message-circle" />
        {commentCount > 0 && <span>{commentCount}</span>}
      </span>
      <button type="button" className={styles.actionBtn} aria-label={t('comments.share')} onClick={onShare}>
        <i className="ti ti-share-3" aria-hidden="true" />
      </button>
      {presentFaces.length > 0 && (
        <span className={styles.faces}>{presentFaces.join('')}</span>
      )}
    </div>
  );

  const threadEl = (
    <div className={styles.thread}>
      {loading && <p className={styles.muted}>…</p>}
      {error && <p className={styles.error}>{error}</p>}
      {!loading && !error && roots.length === 0 && (
        <p className={styles.muted}>{t('comments.empty')}</p>
      )}

      {roots.map((c) => (
        <div key={c.id}>
          <CommentItem
            comment={c}
            mode={mode}
            vocab={vocab}
            currentUserGuid={currentUserGuid}
            isSystemAdmin={isSystemAdmin}
            resolveUser={resolveUser}
            onChanged={refetch}
            onReply={(body) => post_(body, c.id)}
          />
          <div className={styles.replies}>
            {(repliesByParent.get(c.id) ?? []).map((rep) => (
              <CommentItem
                key={rep.id}
                comment={rep}
                mode={mode}
                vocab={vocab}
                currentUserGuid={currentUserGuid}
                isSystemAdmin={isSystemAdmin}
                resolveUser={resolveUser}
                onChanged={refetch}
                isReply
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const composerEl = (
    <div className={styles.composer}>
      <textarea
        className={styles.textarea}
        value={newBody}
        placeholder={t('comments.placeholder')}
        onChange={(e) => setNewBody(e.target.value)}
      />
      <button type="button" className={styles.primaryBtn} disabled={posting || !newBody.trim()} onClick={onPostRoot}>
        {t('comments.post')}
      </button>
    </div>
  );

  const shell = isTheater ? (
    // ── Theater: media stage + comments panel ──────────────────────
    <div
      className={styles.theaterBackdrop}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button type="button" className={styles.theaterClose} aria-label={t('comments.cancel')} onClick={onClose}>
        <i className="ti ti-x" aria-hidden="true" />
      </button>

      <div className={styles.stage} ref={stageRef}>
        <div className={styles.stageControls}>
          <button type="button" aria-label={t('comments.zoomOut')} disabled={zoom <= 1}
            onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}>
            <i className="ti ti-zoom-out" aria-hidden="true" />
          </button>
          <button type="button" aria-label={t('comments.zoomIn')} disabled={zoom >= 3}
            onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}>
            <i className="ti ti-zoom-in" aria-hidden="true" />
          </button>
          <button type="button" aria-label={t('comments.fullscreen')} onClick={toggleFullscreen}>
            <i className="ti ti-arrows-maximize" aria-hidden="true" />
          </button>
        </div>
        <img
          className={styles.stageImage}
          src={mediaSrc(post!.imageUrl!) ?? undefined}
          style={{ transform: `scale(${zoom})` }}
          alt=""
        />
      </div>

      <aside className={styles.panel} role="dialog" aria-modal="true">
        <div className={styles.panelScroll}>
          {postHeadEl}
          {post?.body && <p className={styles.postBody}>{post.body}</p>}
          {openLinkEl}
          {actionRowEl}
          {threadEl}
        </div>
        {composerEl}
      </aside>
    </div>
  ) : (
    // ── Centered modal (default) ───────────────────────────────────
    <div
      className={styles.backdrop}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.modal} role="dialog" aria-modal="true">
        <header className={styles.header}>
          <span className={styles.title}>{headerTitle}</span>
          <button type="button" className={styles.closeBtn} aria-label={t('comments.cancel')} onClick={onClose}>
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </header>

        <div className={styles.scroll}>
          {postHeadEl}
          {post?.imageUrl && (
            <img className={styles.postImage} src={mediaSrc(post.imageUrl) ?? undefined} alt="" loading="lazy" />
          )}
          {post?.body && <p className={styles.postBody}>{post.body}</p>}
          {openLinkEl}
          {actionRowEl}
          {threadEl}
        </div>

        {composerEl}
      </div>
    </div>
  );

  return createPortal(shell, document.body);
}

// ── One comment row (root or reply) ─────────────────────────────────
interface CommentItemProps {
  comment: CommentDto;
  mode: CommentContextMode;
  vocab: ReactionVocabItem[];
  currentUserGuid: string;
  isSystemAdmin: boolean;
  resolveUser?: (guid: string) => ResolvedUser | undefined;
  onChanged: () => void;
  onReply?: (body: string) => Promise<void>;
  isReply?: boolean;
}

function CommentItem({
  comment,
  mode,
  vocab,
  currentUserGuid,
  isSystemAdmin,
  resolveUser,
  onChanged,
  onReply,
  isReply,
}: CommentItemProps) {
  const { t } = useAppTranslation('common');
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState(comment.body);
  const [busy, setBusy] = useState(false);
  const [reported, setReported] = useState(false);

  const isAuthor = comment.authorGuid.toUpperCase() === currentUserGuid.toUpperCase();
  const resolved = resolveUser?.(comment.authorGuid);
  const displayName = isAuthor
    ? t('comments.you')
    : comment.authorName ?? resolved?.name ?? `…${comment.authorGuid.slice(0, 6)}`;
  const avatarSrc = comment.authorAvatarUrl
    ? (mediaSrc(comment.authorAvatarUrl) ?? undefined)
    : resolved?.avatarUrl;

  if (comment.isDeleted) {
    return (
      <div className={`${styles.comment} ${isReply ? styles.commentReply : ''}`}>
        <div className={styles.avatarMuted} aria-hidden="true"><i className="ti ti-trash" /></div>
        <div className={styles.commentMain}>
          <span className={styles.deleted}>
            {comment.deletedByKind === 'moderator' ? t('comments.deletedByModerator') : t('comments.deletedByAuthor')}
          </span>
        </div>
      </div>
    );
  }

  const submitReply = async () => {
    if (!onReply || busy || !replyText.trim()) return;
    setBusy(true);
    try {
      await onReply(replyText);
      setReplyText('');
      setReplyOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const submitEdit = async () => {
    if (busy || !editText.trim()) return;
    setBusy(true);
    try {
      await editComment(comment.id, editText);
      setEditOpen(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteComment(comment.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const doReport = async () => {
    if (busy || reported) return;
    setBusy(true);
    try {
      await reportComment(comment.id, 'inappropriate');
      setReported(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${styles.comment} ${isReply ? styles.commentReply : ''}`}>
      {avatarSrc ? (
        <img className={styles.avatar} src={avatarSrc} alt="" />
      ) : (
        <div className={styles.avatar}>{initials(displayName)}</div>
      )}
      <div className={styles.commentMain}>
        <div className={styles.metaRow}>
          <span className={styles.name}>{displayName}</span>
          <span className={styles.when}>· {formatWhen(comment.createdAt)}</span>
          {comment.edited && <span className={styles.when}>· {t('comments.edited')}</span>}
        </div>

        {editOpen ? (
          <div className={styles.editRow}>
            <textarea className={styles.textarea} value={editText} onChange={(e) => setEditText(e.target.value)} />
            <div className={styles.inlineActions}>
              <button type="button" className={styles.primaryBtn} disabled={busy} onClick={submitEdit}>
                {t('comments.save')}
              </button>
              <button type="button" className={styles.textBtn} onClick={() => setEditOpen(false)}>
                {t('comments.cancel')}
              </button>
            </div>
          </div>
        ) : comment.bodyFormat === 'richtext' ? (
          <div className={styles.bodyRich} dangerouslySetInnerHTML={{ __html: sanitizeRichText(comment.body) }} />
        ) : (
          <div className={styles.commentBody}>{comment.body}</div>
        )}

        {!editOpen && (
          <ReactionBar
            commentId={comment.id}
            reactions={comment.reactions}
            vocab={vocab}
            mode={mode}
            onChanged={onChanged}
          />
        )}

        {!editOpen && (
          <div className={styles.actions}>
            {!isReply && onReply && (
              <button type="button" className={styles.textBtn} onClick={() => setReplyOpen((v) => !v)}>
                <i className="ti ti-arrow-back-up" aria-hidden="true" /> {t('comments.reply')}
              </button>
            )}
            {comment.canEdit && (
              <button type="button" className={styles.textBtn} onClick={() => { setEditText(comment.body); setEditOpen(true); }}>
                <i className="ti ti-edit" aria-hidden="true" /> {t('comments.edit')}
              </button>
            )}
            {(isAuthor || isSystemAdmin) && (
              <button type="button" className={styles.textBtn} onClick={onDelete}>
                <i className="ti ti-trash" aria-hidden="true" /> {t('comments.delete')}
              </button>
            )}
            {!isAuthor && (
              <button type="button" className={styles.textBtn} disabled={reported} onClick={doReport}>
                <i className="ti ti-flag" aria-hidden="true" /> {reported ? t('comments.reported') : t('comments.report')}
              </button>
            )}
          </div>
        )}

        {replyOpen && !isReply && (
          <div className={styles.editRow}>
            <textarea
              className={styles.textarea}
              value={replyText}
              placeholder={t('comments.replyPlaceholder')}
              onChange={(e) => setReplyText(e.target.value)}
            />
            <div className={styles.inlineActions}>
              <button type="button" className={styles.primaryBtn} disabled={busy || !replyText.trim()} onClick={submitReply}>
                {t('comments.reply')}
              </button>
              <button type="button" className={styles.textBtn} onClick={() => setReplyOpen(false)}>
                {t('comments.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationModal;
